import { Check } from 'lucide-react';
import logoEmblem from '@/components/images/logo-amblem.png';
import './EmblemShowcase.scss';

interface EmblemShowcaseProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  bullets?: string[];
}

/**
 * Right-column brand hero for the auth pages.
 *
 * Reads as an operator-console hero, not a marketing splash: the 3D emblem
 * carries the brand, a small OPERATIONAL · LIVE pill sells the realtime
 * nature of the product, and the rest is restrained slate-mono typography.
 *
 * Brand-green appears in exactly three places on this surface — the
 * status dot, the ✓ in bullets, and a faint horizon halo. Everything
 * else is neutral so the emblem reads as the only "lit" element.
 */
export const EmblemShowcase = ({
  eyebrow,
  title,
  subtitle,
  bullets,
}: EmblemShowcaseProps) => {
  return (
    <div className="emblem-showcase">
      <div className="emblem-showcase__status" aria-hidden="true">
        <span className="emblem-showcase__status-dot" />
        <span className="emblem-showcase__status-label">OPERATIONAL</span>
        <span className="emblem-showcase__status-sep">·</span>
        <span className="emblem-showcase__status-tail">LIVE</span>
      </div>

      <div className="emblem-showcase__visual" aria-hidden="true">
        {/* Concentric rings — now neutral slate-white so the emblem is the
            only brand-colored element on the surface. Outer is dashed and
            spins slowly, inner is an arc spinning the opposite way. */}
        <span className="emblem-showcase__ring emblem-showcase__ring--outer" />
        <span className="emblem-showcase__ring emblem-showcase__ring--inner" />
        <span className="emblem-showcase__glow" />
        <img src={logoEmblem} alt="" className="emblem-showcase__emblem" />
      </div>

      <div className="emblem-showcase__copy">
        {eyebrow && <span className="emblem-showcase__eyebrow">{eyebrow}</span>}
        <h2 className="emblem-showcase__title">{title}</h2>
        {subtitle && <p className="emblem-showcase__sub">{subtitle}</p>}

        {bullets && bullets.length > 0 && (
          <ul className="emblem-showcase__bullets">
            {bullets.map((b) => (
              <li key={b} className="emblem-showcase__bullet">
                <span className="emblem-showcase__check" aria-hidden="true">
                  <Check size={10} strokeWidth={3.5} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
